import { ActionButton, ICalloutContentStyles, IChoiceGroupOption, IChoiceGroupOptionProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPChoiceGroup,
  IPCommandBarButton,
  IPDefaultButton,
  IPLabel,
  IPPrimaryButton,
  IPSeparator,
  IPText,
} from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption, IPSelectableOptionMenuItemType } from 'baseComponents/Utils/SelectableOption';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import { globalStackTokensGapSmall, globalTextStylesDisabled } from 'globalStyles';
import { AuditYearStart } from 'models/setting';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { darkTheme, lightTheme } from 'themes/runtime/globalThemes-v8';
import {
  addDateYears,
  fromApiDateOptional,
  getAuditStartForSetting,
  getCurrentAuditYear,
  getDateTimeDiffDays,
  toApiDateOptional,
  toLocaleDateShort,
} from 'utils/datetime';
import { LocalStorageKeys, getLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import { getPeriodFilterEndDate, getPeriodFilterStartDate } from './PeriodFilterHelper';

export enum PeriodFilterDataOption {
  NotSet = '',
  All = 'All',
  AuditYear = 'AuditYear',
  LastWeek = 'LastWeek',
  LastMonth = 'LastMonth',
  LastQuarter = 'LastQuarter',
  LastYear = 'LastYear',
  Custom = 'Custom',
}

export interface IPeriodFilterData {
  option?: PeriodFilterDataOption;
  auditYear?: number;
  customStart?: string;
  customEnd?: string;
}

interface IPeriodFilterProps {
  storageKey: LocalStorageKeys | undefined;
  label?: string;
  showAll?: boolean;
  // the caption of the All option and of the button while it is chosen; "No filter" when not given
  allText?: string;
  showOption?: boolean;
  showCurrentAuditYear?: boolean;
  period?: IPeriodFilterData;
  disabled?: boolean;
  onUpdatePeriod?: (start: Date, end: Date, init?: boolean) => void;
  onUpdatePeriodAll?: (init?: boolean) => void;
  onUpdatePeriodOption?: (period: IPeriodFilterData, init?: boolean) => void;
}

const PeriodFilter = (props: IPeriodFilterProps) => {
  const { t } = useTranslation(['translation', 'dateTimeComponent', 'tasks']);
  const appContext = useContext(AppContext);
  const [showCallOut, setShowCallOut] = useState<boolean>(false);
  const [showCustom, setShowCustom] = useState<boolean>(false);
  const [periodStart, setPeriodStart] = useState<Date | undefined>(undefined);
  const [periodEnd, setPeriodEnd] = useState<Date | undefined>(undefined);
  const [auditDateSetting] = useState<string | undefined>(appContext.globalDataCache.settings.get(AuditYearStart));
  //new state for the callout
  const [newSelectedOption1, setNewSelectedOption1] = useState<PeriodFilterDataOption | undefined>(undefined);
  const [newSelectedOption2, setNewSelectedOption2] = useState<PeriodFilterDataOption | undefined>(undefined);
  const [newSelectedAuditYear, setNewSelectedAuditYear] = useState<number | undefined>(undefined);
  const [newCustomStart, setNewCustomStart] = useState<Date | undefined>(undefined);
  const [newCustomEnd, setNewCustomEnd] = useState<Date | undefined>(undefined);
  //actual state
  const [selectedOption1, setSelectedOption1] = useState<PeriodFilterDataOption | undefined>(undefined);
  const [selectedOption2, setSelectedOption2] = useState<PeriodFilterDataOption | undefined>(undefined);
  const [selectedAuditYear, setSelectedAuditYear] = useState<number | undefined>(undefined);
  const [customStart, setCustomStart] = useState<Date | undefined>(undefined);
  const [customEnd, setCustomEnd] = useState<Date | undefined>(undefined);

  //
  // Effects
  //
  useEffect(() => {
    initFromCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.period) {
      initFromPeriod(props.period);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.period]);

  //
  // Helpers
  //
  const initFromPeriod = (data: IPeriodFilterData) => {
    let customStart: Date | undefined;
    let customEnd: Date | undefined;
    let option1: PeriodFilterDataOption = PeriodFilterDataOption.NotSet;
    let option2: PeriodFilterDataOption = PeriodFilterDataOption.NotSet;

    let auditYear = data.auditYear;
    if (auditYear === undefined) {
      //init last used audit year from storage: get the last used audit year
      const yearFromStorageRaw = getLocalStorageData(appContext, LocalStorageKeys.AuditYear);
      const yearFromStorage = yearFromStorageRaw ? Number.parseInt(yearFromStorageRaw) : undefined;
      auditYear = yearFromStorage ?? getCurrentAuditYear(auditDateSetting);
      data.auditYear = auditYear;
    }
    setSelectedAuditYear(auditYear);

    //determine saved options
    if (
      data.option === PeriodFilterDataOption.AuditYear ||
      data.option === PeriodFilterDataOption.Custom ||
      data.option === PeriodFilterDataOption.All
    ) {
      option2 = data.option;
    } else if (data.option) {
      option1 = data.option;
    } else {
      option2 = PeriodFilterDataOption.AuditYear;
    }

    //always set saved custom dates for when the user selects custom => show the latest saved values
    customStart = fromApiDateOptional(data.customStart);
    customEnd = fromApiDateOptional(data.customEnd);
    setShowCustom(data.option === PeriodFilterDataOption.Custom);

    //set saved/default options
    setSelectedOption1(option1);
    setSelectedOption2(option2);

    //set saved/default custom dates
    if (!customStart) customStart = addDateYears(new Date(), -1);
    if (!customEnd) customEnd = new Date();
    setCustomStart(customStart);
    setCustomEnd(customEnd);

    //calculate period
    const start = getStartDate(option1, option2, auditYear, customStart);
    const end = getEndDate(option1, option2, auditYear, customEnd);

    //always call onUpdate with the loaded/default settings
    updatePeriod(start, end, true);

    if (props.onUpdatePeriodOption) {
      props.onUpdatePeriodOption(data, true);
    }
  };

  const initFromCache = () => {
    //init period from storage
    let data: IPeriodFilterData;
    let value: string | undefined = undefined;

    if (props.storageKey) {
      value = getLocalStorageData(appContext, props.storageKey);
    }

    if (value) {
      data = JSON.parse(value);
    } else {
      //no value stored yet, create default
      data = getDefaultValue();
    }

    initFromPeriod(data);
  };

  const getDefaultValue = (): IPeriodFilterData => {
    return {
      option: props.showAll ? PeriodFilterDataOption.All : PeriodFilterDataOption.AuditYear,
      auditYear: 0,
      customStart: undefined,
      customEnd: undefined,
    };
  };

  const updatePeriod = (start: Date | undefined, end: Date | undefined, init: boolean) => {
    if (
      !periodStart ||
      !periodEnd ||
      !start ||
      !end ||
      getDateTimeDiffDays(start, periodStart) !== 0 ||
      getDateTimeDiffDays(end, periodEnd) !== 0
    ) {
      setPeriodStart(start);
      setPeriodEnd(end);
      if (start && end && props.onUpdatePeriod) {
        props.onUpdatePeriod(start, end, init);
      } else if (props.showAll && props.onUpdatePeriodAll) {
        props.onUpdatePeriodAll(init);
      }
    }
  };

  const getSummaryStack = (): JSX.Element => {
    return (
      <IPStack horizontalAlign="start">
        <IPStack.Item styles={{ root: { paddingLeft: 5 } }}>
          <IPText variant="small" styles={globalTextStylesDisabled}>
            {props.label ?? t('translation:PeriodSelector.Period')}
          </IPText>
        </IPStack.Item>
        <IPStack.Item styles={{ root: { paddingLeft: 5 } }}>{getSummary()}</IPStack.Item>
      </IPStack>
    );
  };

  const getSummary = (): string => {
    if (!periodStart || !periodEnd) {
      return props.allText ?? t('translation:PeriodSelector.All');
    } else {
      if (props.showOption) {
        const option = selectedOption1 || selectedOption2 || PeriodFilterDataOption.NotSet;
        if (option === PeriodFilterDataOption.AuditYear) {
          return `${getYearText(selectedAuditYear ?? 0, auditDateSetting)}`;
        } else if (option === PeriodFilterDataOption.Custom) {
          const start = toLocaleDateShort(customStart);
          const end = toLocaleDateShort(customEnd);
          const to = t('translation:PeriodSelector.To');

          return `${getOptionText(option)} - ${start} ${to} ${end}`;
        } else {
          return `${getOptionText(option)}`;
        }
      } else {
        const start = toLocaleDateShort(periodStart);
        const end = toLocaleDateShort(periodEnd);
        const to = t('translation:PeriodSelector.To');

        return `${start} ${to} ${end}`;
      }
    }
  };

  const onChange1 = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
    setNewSelectedOption1(option?.key as PeriodFilterDataOption);
    setNewSelectedOption2(undefined);
    setShowCustom(false);
  };

  const onChange2 = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
    setNewSelectedOption1(undefined);
    setNewSelectedOption2(option?.key as PeriodFilterDataOption);
    if (option?.key === PeriodFilterDataOption.Custom) {
      setNewCustomStart(newCustomStart ?? periodStart);
      setNewCustomEnd(newCustomEnd ?? periodEnd);
      setShowCustom(true);
    } else {
      setShowCustom(false);
    }
  };

  //
  // Audit year selection
  //
  const yearStartsInJan = (auditDateSetting: string | undefined): boolean => {
    if (!auditDateSetting) return false;
    const month = getAuditStartForSetting(1, auditDateSetting).getMonth();

    return month === 0;
  };

  const getYearText = (yearIdx: number, auditDateSetting: string | undefined) => {
    if (yearIdx === 0) {
      const currentYear = getCurrentAuditYear(auditDateSetting);
      if (yearStartsInJan(auditDateSetting)) {
        return t('tasks:TaskView.Commands.AuditYear.CurrentItemStartInJan', { year: currentYear });
      } else {
        return t('tasks:TaskView.Commands.AuditYear.CurrentItem', { year: currentYear, nextYear: currentYear + 1 });
      }
    } else {
      if (yearStartsInJan(auditDateSetting)) {
        return t('tasks:TaskView.Commands.AuditYear.ItemStartInJan', { year: yearIdx });
      } else {
        return t('tasks:TaskView.Commands.AuditYear.Item', { year: yearIdx, nextYear: yearIdx + 1 });
      }
    }
  };

  /**
   * Get default year items for the audit year dropdown.
   * @param year the current audit year
   * @returns an array of selectable option groups for the audit year. The last group has no items and should be filled later.
   */
  const getDefaultYearItems = (year: number): IPSelectableOption[] => {
    const options: IPSelectableOption[] = [];

    if (props.showCurrentAuditYear) {
      options.push({
        key: 'headerCurrent',
        text: t('tasks:TaskView.Commands.AuditYear.HeaderCurrent'),
        itemType: IPSelectableOptionMenuItemType.Header,
      });
      options.push({
        key: 'yearCurrent0',
        text: getYearText(0, auditDateSetting), //0 means calculate the current audit year
        data: 0,
      });
    }

    options.push({
      key: 'headerCurrent',
      text: t('tasks:TaskView.Commands.AuditYear.HeaderPrevNext'),
      itemType: IPSelectableOptionMenuItemType.Header,
    });

    options.push(
      ...[
        {
          key: 'yearMinus1' + (year - 1).toString(),
          text: getYearText(year - 1, auditDateSetting),
          data: year - 1,
        },
        {
          key: 'yearCurrent' + year.toString(),
          text: getYearText(year, auditDateSetting),
          data: year,
        },
        {
          key: 'yearPlus1' + (year + 1).toString(),
          text: getYearText(year + 1, auditDateSetting),
          data: year + 1,
        },
      ],
    );

    options.push({
      key: 'headerCurrent',
      text: t('tasks:TaskView.Commands.AuditYear.HeaderAll'),
      itemType: IPSelectableOptionMenuItemType.Header,
    });

    return options;
  };

  const getAuditYearOptions = (selectedYear: number): IPSelectableOption[] => {
    const currentYear = selectedYear === 0 ? getCurrentAuditYear(auditDateSetting) : selectedYear;
    const yearMenuProps: IPSelectableOption[] = [...getDefaultYearItems(currentYear)];
    yearMenuProps.push(
      ...[
        ...Array.from({ length: 11 }, (_, i) => {
          const yearIdx: number = i + currentYear - 5;

          return {
            key: 'year' + yearIdx.toString(),
            text: getYearText(yearIdx, auditDateSetting),
            data: yearIdx,
          };
        }),
      ],
    );

    return yearMenuProps;
  };

  const savePrevNextAuditYear = (diff: number) => {
    let currentYear = newSelectedAuditYear ?? selectedAuditYear ?? getCurrentAuditYear(auditDateSetting);
    //fix invalid values like 0 or values from before 2000 by resetting to the current audit year
    if (currentYear < 2000) currentYear = getCurrentAuditYear(auditDateSetting);
    const newYear = currentYear + diff;
    setNewSelectedAuditYear(newYear);
    onApply(newSelectedOption1, newSelectedOption2, newYear, newCustomStart, newCustomEnd);
  };

  const onRenderAuditYear = (
    itemProps?: IChoiceGroupOption & IChoiceGroupOptionProps,
    defaultRender?: (props?: IChoiceGroupOption & IChoiceGroupOptionProps) => JSX.Element | null,
  ): JSX.Element | null => {
    if (!defaultRender || newSelectedAuditYear === undefined) return null;
    if (itemProps && itemProps.key === PeriodFilterDataOption.AuditYear && itemProps.checked) {
      return (
        <IPStack tokens={globalStackTokensGapSmall}>
          <IPStack.Item>{defaultRender(itemProps)}</IPStack.Item>
          <IPStack.Item>
            <IPDropdown
              selectedKey={'yearCurrent' + newSelectedAuditYear}
              options={getAuditYearOptions(newSelectedAuditYear)}
              styles={{ listbox: { maxHeight: 250 } }}
              onChange={(ev, option) => setNewSelectedAuditYear(option?.data)}
            />
          </IPStack.Item>
          <IPStack horizontal tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <ActionButton styles={{ root: { height: 20 } }} onClick={() => savePrevNextAuditYear(-1)}>
                {t('translation:General.Button.Prev')}
              </ActionButton>
            </IPStack.Item>
            <IPStack.Item>
              <ActionButton styles={{ root: { height: 20 } }} onClick={() => savePrevNextAuditYear(1)}>
                {t('translation:General.Button.Next')}
              </ActionButton>
            </IPStack.Item>
          </IPStack>
        </IPStack>
      );
    } else {
      return defaultRender(itemProps);
    }
  };

  //
  // Calc result
  //
  const getStartDate = (
    option1: PeriodFilterDataOption | undefined,
    option2: PeriodFilterDataOption | undefined,
    selectedAuditYear: number | undefined,
    customStart: Date | undefined,
  ): Date | undefined => {
    return getPeriodFilterStartDate(option1 || option2, selectedAuditYear, customStart, appContext);
  };

  const getEndDate = (
    option1: PeriodFilterDataOption | undefined,
    option2: PeriodFilterDataOption | undefined,
    selectedAuditYear: number | undefined,
    customEnd: Date | undefined,
  ): Date | undefined => {
    return getPeriodFilterEndDate(option1 || option2, selectedAuditYear, customEnd, appContext);
  };

  const onApply = (
    newSelectedOption1: PeriodFilterDataOption | undefined,
    newSelectedOption2: PeriodFilterDataOption | undefined,
    newSelectedAuditYear: number | undefined,
    newCustomStart: Date | undefined,
    newCustomEnd: Date | undefined,
  ) => {
    const start = getStartDate(newSelectedOption1, newSelectedOption2, newSelectedAuditYear, newCustomStart);
    const end = getEndDate(newSelectedOption1, newSelectedOption2, newSelectedAuditYear, newCustomEnd);

    updatePeriod(start, end, false);
    setShowCallOut(false);

    if (props.storageKey) {
      const data: IPeriodFilterData = {
        option: newSelectedOption1 || newSelectedOption2,
        customStart: toApiDateOptional(newCustomStart),
        customEnd: toApiDateOptional(newCustomEnd),
        //the audit year is stored seperately because the user expects the audit year to be remembered over different views
      };

      setLocalStorageData(appContext, props.storageKey, JSON.stringify(data));
    }

    if (newSelectedAuditYear && newSelectedOption2 === PeriodFilterDataOption.AuditYear) {
      setLocalStorageData(appContext, LocalStorageKeys.AuditYear, newSelectedAuditYear.toString());
    }

    if (props.onUpdatePeriodOption) {
      const data: IPeriodFilterData = {
        option: newSelectedOption1 || newSelectedOption2,
        customStart: toApiDateOptional(newCustomStart),
        customEnd: toApiDateOptional(newCustomEnd),
        //the audit year is returned as part of the period data because it is requested by the caller
        //use cases are widgets that store the audit year as the selected period
        auditYear: newSelectedAuditYear,
      };

      props.onUpdatePeriodOption(data, false);
    }

    //update the current state
    setSelectedOption1(newSelectedOption1);
    setSelectedOption2(newSelectedOption2);
    setSelectedAuditYear(newSelectedAuditYear);
    setCustomStart(newCustomStart);
    setCustomEnd(newCustomEnd);
  };

  //
  // Options
  //
  const calloutStyle: Partial<ICalloutContentStyles> = {
    root: {
      padding: 20,
      minWidth: 350,
      maxWidth: 600,
      background: appContext.useDarkMode ? darkTheme.palette?.white : lightTheme.palette?.white,
    },
  };

  const getOptionText = (option: PeriodFilterDataOption): string => {
    //return the option text based on a switch statement
    switch (option) {
      case PeriodFilterDataOption.LastWeek:
        return t('translation:PeriodSelector.LastWeek');
      case PeriodFilterDataOption.LastMonth:
        return t('translation:PeriodSelector.LastMonth');
      case PeriodFilterDataOption.LastQuarter:
        return t('translation:PeriodSelector.LastQuarter');
      case PeriodFilterDataOption.LastYear:
        return t('translation:PeriodSelector.LastYear');
      case PeriodFilterDataOption.All:
        return props.allText ?? t('translation:PeriodSelector.All');
      case PeriodFilterDataOption.AuditYear:
        return t('translation:PeriodSelector.AuditYear');
      case PeriodFilterDataOption.Custom:
        return t('translation:PeriodSelector.Custom');
      default:
        return '';
    }
  };

  const getOptions1 = (): IChoiceGroupOption[] => {
    return [
      { key: PeriodFilterDataOption.LastWeek, text: t('translation:PeriodSelector.LastWeek') },
      { key: PeriodFilterDataOption.LastMonth, text: t('translation:PeriodSelector.LastMonth') },
      { key: PeriodFilterDataOption.LastQuarter, text: t('translation:PeriodSelector.LastQuarter') },
      { key: PeriodFilterDataOption.LastYear, text: t('translation:PeriodSelector.LastYear') },
    ];
  };

  const getOptions2 = (): IChoiceGroupOption[] => {
    const options: IChoiceGroupOption[] = [];
    if (props.showAll) {
      options.push({ key: PeriodFilterDataOption.All, text: props.allText ?? t('translation:PeriodSelector.All') });
    }

    options.push(
      ...[
        {
          key: PeriodFilterDataOption.AuditYear,
          text: t('translation:PeriodSelector.AuditYear'),
          onRenderField: onRenderAuditYear,
        },
        { key: PeriodFilterDataOption.Custom, text: t('translation:PeriodSelector.Custom') },
      ],
    );

    return options;
  };

  const setCalloutState = (enabled: boolean) => {
    setShowCallOut(enabled);
    if (enabled) {
      //reset the new state to the current state
      setNewSelectedOption1(selectedOption1);
      setNewSelectedOption2(selectedOption2);
      setNewSelectedAuditYear(selectedAuditYear);
      setNewCustomStart(customStart);
      setNewCustomEnd(customEnd);
    }
  };

  //
  // Main render
  //
  return (
    <Fragment>
      <IPCommandBarButton
        iconProps={{ iconName: 'Calendar' }}
        id="period-selector-callout"
        onClick={() => setCalloutState(!showCallOut)}
        styles={{ root: { minWidth: 200, height: 44, justifyContent: 'flex-start' } }}
        disabled={props.disabled}
        onRenderText={() => getSummaryStack()}
      />
      <IPCallout
        hidden={!showCallOut}
        styles={calloutStyle}
        setInitialFocus
        gapSpace={0}
        isBeakVisible={false}
        directionalHint={IPDirectionalHint.bottomLeftEdge}
        target={'#period-selector-callout'}
        onDismiss={() => {
          setCalloutState(false);
        }}
      >
        <IPStack tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <IPLabel>{props.label ?? t('translation:PeriodSelector.Period')}</IPLabel>
          </IPStack.Item>
          <IPStack.Item>
            <IPStack horizontal tokens={globalStackTokensGapSmall}>
              <IPStack.Item>
                <IPChoiceGroup selectedKey={newSelectedOption1 || ''} options={getOptions1()} onChange={onChange1} />
              </IPStack.Item>
              <IPSeparator vertical />
              <IPStack.Item>
                <IPStack tokens={globalStackTokensGapSmall}>
                  <IPChoiceGroup selectedKey={newSelectedOption2 || ''} options={getOptions2()} onChange={onChange2} />
                  {showCustom && (
                    <IPStack tokens={globalStackTokensGapSmall}>
                      <IPStack.Item>
                        <LocalizedDatePicker
                          showMonthPickerAsOverlay={false}
                          showWeekNumbers={true}
                          value={newCustomStart}
                          maxDate={newCustomEnd}
                          onDateChange={(date: Date | undefined) => {
                            if (date) setNewCustomStart(date);
                          }}
                          format="short"
                          styles={{ root: { minWidth: 150 } }}
                        />
                      </IPStack.Item>
                      <IPStack.Item>
                        <LocalizedDatePicker
                          showMonthPickerAsOverlay={false}
                          showWeekNumbers={true}
                          value={newCustomEnd}
                          minDate={newCustomStart}
                          onDateChange={(date: Date | undefined) => {
                            if (date) setNewCustomEnd(date);
                          }}
                          format="short"
                          styles={{ root: { minWidth: 150 } }}
                        />
                      </IPStack.Item>
                    </IPStack>
                  )}
                </IPStack>
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
          <IPSeparator />
          <IPStack horizontal horizontalAlign="end" tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPPrimaryButton
                text={t('translation:General.Button.Select')}
                onClick={() =>
                  onApply(newSelectedOption1, newSelectedOption2, newSelectedAuditYear, newCustomStart, newCustomEnd)
                }
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPDefaultButton text={t('translation:General.Button.Cancel')} onClick={() => setCalloutState(false)} />
            </IPStack.Item>
          </IPStack>
        </IPStack>
      </IPCallout>
    </Fragment>
  );
};

export default PeriodFilter;
